import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useIntl, useSelector, getLocale } from 'umi';
import moment from 'moment';
import WechatGamesMoreFilter, {
  IMgrMoreFilterInstance
} from '@intelligence/v3/pages/ReusePages/CasualMiniGameRankings/components/WechatGamesMoreFilter';
import DopuyinGamesMoreFilter from '@intelligence/v3/pages/ReusePages/CasualMiniGameRankings/components/DopuyinGamesMoreFilter';
import ExportTable from '@/pages/intelligence/components/ExportTable';
import WebchatStoreRankMoreCore from '@intelligence/v3/pages/ReusePages/CasualMiniGameRankings/components/WebchatStoreRankMoreCore';
import useFilterExtend from '@/pages/intelligence/hooks/useFilterExtend';
import useInitialTopChartsName from '@intelligence/v3/pages/ReusePages/CasualMiniGameRankings/hooks/useInitialTopChartsName';
import useBuriedPointLogCfgKeys from '@/pages/intelligence/pages/StoreRanks/components/StoreRankMoreCore/hooks/useBuriedPointLogCfgKeys';
import { FILTER_EXTEND_NAMESPACE } from '@/pages/intelligence/models/filterExtend';
import * as FILTER_KEYS from '@/pages/intelligence/components/Filter/FilterBase/_config';
import { LANGUAGE_CN, LANGUAGE_EN } from '@intelligence/_config/config';
import {
  GAME_KEY,
  MOBILE_SECTIONAL_RANK_KEYS,
  MOBILE_SECTIONAL_RANK_INFOS,
  WECHAT_SOURCES_METRICS_KEYS,
  WECHAT_PLATFORM_TOP_INFOS,
  MOBILE_SOURCE_APPSTORE_TOP_FREE,
} from '@/pages/intelligence/_config/storeRank/config';
import { handleRangeMiniGameMoreFilterExtend } from '@intelligence/utils/dateUtils';
import {
  FilterTimeType,
} from '@intelligence/utils/filterUtils';
import {
  getFilterName,
  getFilterValueStorage,
} from '@/pages/intelligence/components/Filter/util';
import type { IBaseProps } from '@/pages/intelligence/types/common';
import type {
  IMobileStoreRankMoreFilter,
  IStoreRankMoreCoreRankPrimaryKey,
} from '@/pages/intelligence/types/storeRank';
import useEarliesTimeHasData from '@/pages/intelligence/hooks/useEarliesTimeHasData';
import { hasDataTimerParams } from '@intelligence/utils/timerDefautParams';
import useMiniGameRankingsMore from '@intelligence/v3/pages/ReusePages/CasualMiniGameRankings/hooks/useMiniGameRankingsMore';
import useInitialDateRange from '@intelligence/v3/_hooks/useInitialDateRange';
import useMetricsDateRange from '@intelligence/v3/_hooks/useMetricsDateRange';
import {
  MINI_GAME_RANKING_KEY_LIST,
  MINI_GAME_RANKING_MORE_TITLE_MAP,
  MINI_GAME_ENTITY_TYPE,
} from '@intelligence/v3/pages/ReusePages/CasualMiniGameRankings/config';
import { updateURLSearchParams } from '@intelligence/utils/commonUtils';
import { useDeepCompareEffect } from 'ahooks';
import MarqueeBanner from '@intelligence/components/MarqueeBanner/index';
import './index.scss';

const ENTITY_TYPE = 'mobile';
const SEARCH_TYPE_WEIXIN = 'weixin-store-rank-more';
const SEARCH_TYPE_DOUYIN = 'douyin-store-rank-more';
const BUSINESS_TYPE = 'weixin-store-rank-more';
const DEFAULT_FETCH_PARAMS: any = {
  entity_type: MINI_GAME_ENTITY_TYPE,
  topchart_name: MOBILE_SOURCE_APPSTORE_TOP_FREE,
  genre_source: 'weixin',
  granularity: 'daily',
  start_date: '',
  end_date: '',
  order_source: 'wechat_popularity',
  order_metric: 'game_rank',
  order_by: ['game_rank asc'],
  order: 'desc',
  ratio_type: 0,
  page: 1,
  page_size: 100,
};

type IMobileStoreRankMoreProps = IBaseProps;
const getEarliesTimeType = (_name: string) => {
  return hasDataTimerParams?.storerankMobileDetailTopFree?.type;
};

const MiniGameStoreRankMore: React.FC<IMobileStoreRankMoreProps> = () => {
  const { formatMessage } = useIntl();
  const locale = getLocale();
  const isCN = locale === LANGUAGE_CN;
  const isEN = locale === LANGUAGE_EN;
  const { userInfo } = useSelector((state: any) => {
    const { userInfo } = state.home;
    return {
      userInfo,
    };
  });
  // 在筛选器这里拿到缓存的值，并渲染
  const filterName = getFilterName(userInfo);
  const filterData = getFilterValueStorage(userInfo);
  const filterState = useSelector((rootState: any) => rootState?.[FILTER_EXTEND_NAMESPACE]) ?? {};
  const isOperate = !!filterState?.isOperate;

  const { filterExtend } = useFilterExtend({});
  // 筛选器 ref，用于点击品类标签时设置筛选器值并触发请求
  const filterRef = useRef<IMgrMoreFilterInstance | null>(null);
  const initialTopChartsName = useInitialTopChartsName();
  const dateRange = useInitialDateRange();
  const [topchartName, setTopchartName] = useState(initialTopChartsName);

  useDeepCompareEffect(() => {
    setTopchartName(initialTopChartsName);
  }, [initialTopChartsName]);

  const [initialFetchParams, setInitialFetchParams] = useState<any>({
    ...DEFAULT_FETCH_PARAMS,
    topchart_name: topchartName,
    start_date: dateRange?.startDate || '',
    end_date: dateRange?.endDate || '',
    order_source: topchartName,
  });

  const { pageLogCfg, downloadExcelLogCfgKey } = useBuriedPointLogCfgKeys(
    MINI_GAME_ENTITY_TYPE,
    topchartName,
  );

  const { latestTimeTypeHasData } = useEarliesTimeHasData({
    filterType: getEarliesTimeType(topchartName),
    rangeTimeType: FilterTimeType.sameDay,
  });

  const { loading: metricsGranularityDateRangesLoading, granularityDateRangeMap: metricsGranularityDateRangeMap } =
    useMetricsDateRange(true, {
      game_ids: [],
      app_ids: [],
      granularities: ['daily'],
      markets: [],
      metrics: ['minigame__wechat_popularity'],
      group_by: ['date'],
    });

  const getChangeLocalData = useCallback((dataList: any, isENLocal = true) => {
    return dataList?.map((item: any) => {
      return {
        ...item,
        app_name: (isENLocal && item?.app_name_en) || item?.app_name_cn || item?.app_name,
        name: (isENLocal && item?.app_name_en) || item?.app_name_cn || item?.app_name,
        publisher: (isENLocal && item?.publisher_en) || item?.publisher_cn || item?.publisher,
        minigame_genre: (isENLocal && item?.minigame_genre_en) || item?.minigame_genre_cn || item?.minigame_genre,
        minigame_sub_genre: (isENLocal && item?.minigame_sub_genre_en) || item?.minigame_sub_genre_cn || item?.minigame_sub_genre,
        desc: (isENLocal && item?.app_desc_en) || item?.app_desc
      };
    });
  }, [isEN]);

  const {
    fetchParams,
    handleInit,
    handleConfirm,
    handleReset,
    isVisualisLoading,
    isVisualisNoData,
    seriesData,
    xAxisData,
    currentLine,
    segments,
    segment,
    setSegment,
    yAxisFormatter,
    tooltipFormatter,
    createLegendsCustomMade,
    isTableLoading,
    isTableNoData,
    tableDimensionsData,
    tableTotal,
    tableData,
    tableSelection,
    defaultSelectionGames,
    getTableResultList,
    handleClearSearch,
    onTableSortChage,
    onTablePagingChange,
    onTableCheckGame,
    onPageInit,
    allTableData,
    currentPage,
    currentPageSize,
  } = useMiniGameRankingsMore({
    entityType: MINI_GAME_ENTITY_TYPE,
    pageLogCfg,
    primaryKey: GAME_KEY as IStoreRankMoreCoreRankPrimaryKey,
    platformTopMetrics: WECHAT_SOURCES_METRICS_KEYS,
    initialFetchParams,
    initialTopChartsName: topchartName,
    isEN,
    getChangeLocalData,
  });

  useEffect(() => {
    if (!userInfo?.userid || metricsGranularityDateRangesLoading) return;
    let newFetchParams = { ...fetchParams };
    // console.debug('filterNamefilterName', filterName)
    // console.debug('filterState', filterState)
    // console.debug('filterData', filterData)
    if (filterName) {
      const filterSaveParams = getFilterSaveParams();
      // console.debug('filterSaveParams', filterSaveParams)
      newFetchParams = {
        ...newFetchParams,
        ...filterSaveParams,
        topchart_name: topchartName,
        start_date: dateRange?.startDate || moment().format('YYYY-MM-DD'),
        end_date: dateRange?.endDate || moment().format('YYYY-MM-DD'),
      };
    } else if (filterState.isOperate === true) {
      /** 获取继承参数 */
      const extendParams = getExtendParams();
      // console.debug('extendParams0000', extendParams)
      newFetchParams = {
        ...newFetchParams,
        ...extendParams,
      };
    }
    const dateObj = metricsGranularityDateRangeMap[''] || ({} as any);
    if (!newFetchParams.start_date || !newFetchParams.end_date) {
      const today = new Date().toISOString().split('T')[0];
      newFetchParams.start_date = dateObj?.max_date || today;
      newFetchParams.end_date = dateObj?.max_date || today;
    }

    setInitialFetchParams(newFetchParams);
    onPageInit(newFetchParams);
  }, [userInfo?.userid, metricsGranularityDateRangesLoading, metricsGranularityDateRangeMap]);

  const getExtendParams = () => {
    return handleFilterParams(filterExtend);
  };

  const getFilterSaveParams = () => {
    return handleFilterParams(filterData?.apiValue);
  };

  const handleFilterParams = (payload: any) => {
    // console.debug('handleFilterParamspayload', payload)
    const params = handleRangeMiniGameMoreFilterExtend(payload);
    const newFetchParams: any = {};
    const timeExtend = params?.[FILTER_KEYS?.RANGE_TIME];
    const genreExtend = params?.[FILTER_KEYS?.GENRETOR];

    if (timeExtend) {
      newFetchParams.start_date = timeExtend?.start_date;
      newFetchParams.end_date = timeExtend?.end_date;
    }
    if (genreExtend) {
      newFetchParams.minigame_genre = genreExtend?.minigame_genre || [];
    }

    return {
      ...newFetchParams,
      topchart_name: topchartName,
    };
  };

  const handleConfirmInPage = (payload: IMobileStoreRankMoreFilter) => {
    const topChartsNameValue = payload?.single_source_select ?? topchartName;
    handleConfirm(payload, topChartsNameValue);
    if (payload?.single_source_select) {
      setTopchartName(payload?.single_source_select);
      updateURLSearchParams({ source: payload?.single_source_select });
    }
  };

  const isWeChat = useMemo(() => {
    if (MINI_GAME_RANKING_KEY_LIST.includes(topchartName)) {
      return true;
    }
    return false;
  }, [topchartName, MINI_GAME_RANKING_KEY_LIST]);

  const titleCur = useMemo(() => {
    if (MINI_GAME_RANKING_MORE_TITLE_MAP[topchartName]) {
      return formatMessage({ id: MINI_GAME_RANKING_MORE_TITLE_MAP[topchartName] });
    }
    return '';
  }, [fetchParams, topchartName, locale]);

  const gameColumnsWithCur = useMemo(() => {
    if (!isWeChat) {
      return '43%';
    }
    return '33%';
  }, [isWeChat]);

  const searchTypeCur = useMemo(() => {
    if (isWeChat) {
      return SEARCH_TYPE_WEIXIN;
    }
    return SEARCH_TYPE_DOUYIN;
  }, [isWeChat]);

  return (
    <>
      <MarqueeBanner />
      <WebchatStoreRankMoreCore
        entityType={MINI_GAME_ENTITY_TYPE}
      searchType={searchTypeCur}
      pageLogInfo={pageLogCfg}
      title={titleCur}
      isOperate={isOperate}
      isSectional={false}
      isShowVisualis={true}
      isWeChat={isWeChat}
      fetchParams={{ ...fetchParams }}
      pageSortAttr={{
        tablePageIndex: currentPage,
        tablePageSize: currentPageSize,
        currentOrderSource: fetchParams?.order_source,
        currentSortMetric: fetchParams.order_metric,
        currentSortValue: fetchParams.order,
        currentRatioType: fetchParams?.ratio_type,
      }}
      isHasDetailsColumns={false}
      isHasReleaseTimeColumns={false}
      isHasGenreColumns={isWeChat}
      searchDataList={allTableData}
      visualisOptions={{
        isVisualisLoading,
        isVisualisNoData,
        seriesData,
        xAxisData,
        sortKeyName: 'dbi_wechat_sorted_by_rank',
        currentLine,
        segments,
        segment,
        setSegment,
        yAxisFormatter,
        tooltipFormatter,
        createLegendsCustomMade,
      }}
      tablesOptions={{
        isTableLoading,
        isTableNoData,
        // 表格数据的主键，表格的recordKey
        primaryKey: GAME_KEY as typeof GAME_KEY & IStoreRankMoreCoreRankPrimaryKey,
        uunifiedKey: GAME_KEY,
        // 分段排名rank模式时，表格数据的主键，可视化需要用到（获取图片），mobile 分段排名rank模式 暂时没有可视化，所以这个字段在mobile没用
        imageKey: 'cover',
        sectionalRankKeys: MOBILE_SECTIONAL_RANK_KEYS,
        sectionalRankInfos: MOBILE_SECTIONAL_RANK_INFOS,
        platformTopMetrics: WECHAT_SOURCES_METRICS_KEYS,
        platformTopInfos: WECHAT_PLATFORM_TOP_INFOS,
        tableDimensionsData,
        tableTotal,
        tableData: getChangeLocalData(tableData, isEN),
        tableSelection,
        defaultSelectionGames,
        getTableResultList,
        handleClearSearch,
        onTableSortChage,
        onTablePagingChange,
        onTableCheckGame,
        gameColumnsWithCur,
      }}
      filter={isWeChat ? <WechatGamesMoreFilter
        ref={filterRef}
        isOperate={isOperate}
        buttonLogConfig={{}}
        filter={fetchParams}
        onInit={handleInit}
        onConfirm={handleConfirmInPage}
        onReset={handleReset}
        rangeTimeComponentDefault={latestTimeTypeHasData}
        entityType={ENTITY_TYPE}
      /> : <DopuyinGamesMoreFilter
        ref={filterRef}
        isOperate={isOperate}
        buttonLogConfig={{}}
        filter={fetchParams}
        onInit={handleInit}
        onConfirm={handleConfirmInPage}
        onReset={handleReset}
        rangeTimeComponentDefault={latestTimeTypeHasData}
        entityType={ENTITY_TYPE}
        topchartName={topchartName}
      />}
      exportTable={
        <ExportTable
          entityType={ENTITY_TYPE}
          // @ts-ignore
          businessType={BUSINESS_TYPE}
          downloadLogConfigKey={downloadExcelLogCfgKey}
          isDisableDownload={isTableLoading}
          isNeedCheckedLines={true}
          useDownloadAmountType={true}
          downloadModalSize={isCN ? 600 : 684}
          tableParams={{
            ...fetchParams,
            downlaod_date_method: 'aggregated',
            start_date: moment(fetchParams?.end_date ?? '')
              .subtract(30, 'days')
              .format('YYYY-MM-DD'),
            end_date: moment(fetchParams?.end_date ?? '').format('YYYY-MM-DD'),
          }}
          total={tableTotal}
          getCheckedLinesCfg={() => ({
            key: 'app_ids',
            value: tableSelection?.map((item) => item?.game_id) || [],
          })}
          useWebDownload={true}
          downloadKey={'weixinStoreRankMore'}
          newStyleFileName={isWeChat ? formatMessage({ id: 'dbi_store_rank_mini_game_wechat_detail' }): formatMessage({ id: 'dbi_store_rank_mini_game_douyin_detail' })}
          texts={{ top: `${titleCur}${isCN ? '' : ' '}${formatMessage({ id: 'dbi_rank' })}` }}
        />
      }
    />
    </>
  );
};
export default MiniGameStoreRankMore;
